import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import SidebarLayout from '../components/SidebarLayout';
import { SummaryCards } from '../components/SummaryCards';
import { CostChart } from '../components/CostChart';
import { CostOverTimeChart } from '../components/CostOverTimeChart';
import { TraceHierarchyView } from '../components/TraceHierarchyView';
import { TraceTable } from '../components/TraceTable';
import { TraceDetailDrawer } from '../components/TraceDetailDrawer';
import { TimeRangeSelector, getTimeRangeParams } from '../components/TimeRangeSelector';
import { useTraces, useCostSummary, useHealth } from '../hooks/useTraces';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { LiveFeed } from '../components/LiveFeed';

const TIME_RANGE_LABELS = {
  '1h': 'Last 1 hour',
  '6h': 'Last 6 hours',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
};

export const Dashboard = () => {
  const [searchParams] = useSearchParams();
  const { isLoaded: authLoaded, userId } = useAuth();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedTrace, setSelectedTrace] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [timeRange, setTimeRange] = useState({ preset: '7d', startDate: null, endDate: null });

  const tenantFromUrl = searchParams.get('tenant') || searchParams.get('tenant_id') || searchParams.get('slug');
  const tenantFromStorage = localStorage.getItem('tenantSlug');
  const tenant = userId || tenantFromUrl || tenantFromStorage;

  const timeRangeParams = getTimeRangeParams(timeRange);
  const timeRangeLabel = TIME_RANGE_LABELS[timeRange.preset] || timeRange.preset;

  const {
    data: traces = [],
    isLoading: tracesLoading,
    isError: tracesError,
    refetch: refetchTraces
  } = useTraces(
    { tenant, ...timeRangeParams, limit: 100 },
    { autoRefresh, enabled: authLoaded && !!tenant }
  );

  const {
    data: costSummary = [],
    refetch: refetchCosts
  } = useCostSummary(
    { tenant, ...timeRangeParams },
    { autoRefresh, enabled: authLoaded && !!tenant }
  );

  const { data: health } = useHealth();

  const handleTraceClick = (trace) => {
    setSelectedTrace(trace);
    setDrawerOpen(true);
  };

  const handleRefresh = () => {
    refetchTraces();
    refetchCosts();
  };

  if (!authLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-zinc-500 font-mono text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <SidebarLayout>
      <div className="flex flex-col h-screen">
        {/* Top bar with controls */}
        <div className="bg-[#111113] border-b border-white/[0.06] px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold text-white">Dashboard</h1>
            {health?.status === 'healthy' && (
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                <span className="text-xs text-zinc-500 font-mono">Connected</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500">Time range:</span>
              <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-7 h-4 bg-zinc-700 peer-checked:bg-green-600 rounded-full relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-3 after:h-3 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-3" />
              <span className="text-xs text-zinc-500">Auto-refresh</span>
            </label>
            <button
              onClick={handleRefresh}
              className="text-xs text-zinc-400 hover:text-white px-2 py-1 border border-white/[0.08] rounded transition-colors font-mono"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 space-y-6">
          <SummaryCards traces={traces} costSummary={costSummary} timeRangeLabel={timeRangeLabel} />

          {/* Routing Insight Banner */}
          {traces.length >= 3 && (
            <div className="rounded border border-emerald-500/20 bg-emerald-500/5 px-4 py-2.5 flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
              <p className="text-xs text-emerald-400 font-mono">
                <span className="font-semibold">Routing active</span>{" "}
                — {traces.length} traces — Thompson Sampling selecting optimal paths
              </p>
            </div>
          )}
          {traces.length === 0 && !tracesLoading && (
            <div className="rounded border border-zinc-800 bg-zinc-900/40 px-4 py-2.5 flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-zinc-500 flex-shrink-0" />
              <p className="text-xs text-zinc-500 font-mono">
                No traces yet. Run <code className="bg-zinc-800 px-1 py-0.5 rounded">kalibr verify</code> to send your first trace.
              </p>
            </div>
          )}

          <LiveFeed traces={traces} />

          {/* Cost Charts */}
          <div className="grid gap-4 md:grid-cols-2">
            <CostChart costSummary={costSummary} />
            <CostOverTimeChart traces={traces} />
          </div>

          {/* Trace Hierarchy View */}
          {selectedTrace && (
            <TraceHierarchyView spans={traces.filter(t => t.trace_id === selectedTrace)} />
          )}

          {/* Traces Table */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-white uppercase tracking-wider">Recent Traces</h2>
              {tracesLoading && (
                <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono">
                  <div className="animate-spin rounded-full h-3 w-3 border-b border-zinc-400"></div>
                  Loading...
                </div>
              )}
            </div>

            {tracesError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Failed to load traces. Check backend connection.
                </AlertDescription>
              </Alert>
            ) : (
              <TraceTable
                traces={traces}
                onTraceClick={handleTraceClick}
              />
            )}
          </div>
        </div>
      </div>

      <TraceDetailDrawer
        trace={selectedTrace}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </SidebarLayout>
  );
};
